// src/services/hrWorkScheduleService.js
// =============================================================================
// HR Work Schedule Management API service
// =============================================================================
//
// PURPOSE
// -------
// Thin frontend API layer for the operational HR → Work Schedules page.
//
// This service intentionally does not implement schedule priority rules.  The
// backend preview endpoint resolves the authoritative effective minutes/source
// for every employee/day using the same leaveDurationService logic as leave
// request creation.

import apiClient from "../utils/apiClient";
import { isRequestCanceled } from "./httpUtils";

function joinNumericCsv(values) {
  return (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0)
    .join(",");
}

function buildConfig(options = {}) {
  return {
    withCredentials: true,
    signal: options.signal,
  };
}

function getApiErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function throwWithContext(context, error, fallback) {
  const e = new Error(getApiErrorMessage(error, fallback));
  e.status = error?.response?.status;
  e.code = error?.response?.data?.code || error?.code;
  e.data = error?.response?.data;
  console.error(`[hrWorkScheduleService] ❌ ${context}:`, error);
  throw e;
}

/**
 * Fetch the week/work-schedule planning preview.
 *
 * @param {object} params
 * @param {string} params.from - YYYY-MM-DD inclusive
 * @param {string} params.to - YYYY-MM-DD inclusive
 * @param {number[]} [params.userIds]
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{range: object, users: object[], rows: object[], meta: object}>}
 */
export async function fetchWorkSchedulePreview(params = {}, options = {}) {
  try {
    const query = new URLSearchParams();
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);

    const userIds = joinNumericCsv(params.userIds);
    if (userIds) query.set("user_ids", userIds);

    const response = await apiClient.get(
      `/hr/work-schedules/preview?${query.toString()}`,
      buildConfig(options)
    );

    return {
      range: response.data?.range || { from: params.from, to: params.to },
      users: Array.isArray(response.data?.users) ? response.data.users : [],
      rows: Array.isArray(response.data?.rows) ? response.data.rows : [],
      meta: response.data?.meta || {},
    };
  } catch (error) {
    if (isRequestCanceled(error)) throw error;
    throwWithContext("fetchWorkSchedulePreview", error, "Failed to load work schedules.");
  }
}

/**
 * Upsert a one-day employee-specific work schedule override.
 *
 * The route is intentionally date-oriented: saving the same user/date again
 * replaces the currently effective override and reactivates it if it had been
 * deleted before.
 */
export async function saveDailyScheduleOverride(userId, payload) {
  try {
    const response = await apiClient.put(
      `/hr/work-schedules/${userId}/daily-overrides`,
      payload || {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );
    return response.data;
  } catch (error) {
    throwWithContext("saveDailyScheduleOverride", error, "Failed to save daily schedule exception.");
  }
}

/**
 * Soft-delete a one-day work schedule override.
 */
export async function deleteDailyScheduleOverride(userId, overrideId) {
  try {
    const response = await apiClient.delete(
      `/hr/work-schedules/${userId}/daily-overrides/${overrideId}`,
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    throwWithContext("deleteDailyScheduleOverride", error, "Failed to delete daily schedule exception.");
  }
}

/**
 * Load active rotating work-pattern templates that can be assigned to employees.
 * Reception can assign existing templates from the HR scheduling module, while
 * template maintenance remains separated in the master-data/admin area.
 */
export async function fetchWorkPatterns(options = {}) {
  try {
    const response = await apiClient.get(
      "/hr/work-schedules/work-patterns",
      buildConfig(options)
    );
    return Array.isArray(response.data?.patterns) ? response.data.patterns : [];
  } catch (error) {
    if (isRequestCanceled(error)) throw error;
    throwWithContext("fetchWorkPatterns", error, "Failed to load work patterns.");
  }
}

/**
 * Load current and historical rotating-pattern assignments for one employee.
 */
export async function fetchPatternAssignments(userId, options = {}) {
  try {
    const response = await apiClient.get(
      `/hr/work-schedules/${userId}/pattern-assignments`,
      buildConfig(options)
    );
    return Array.isArray(response.data?.assignments) ? response.data.assignments : [];
  } catch (error) {
    if (isRequestCanceled(error)) throw error;
    throwWithContext("fetchPatternAssignments", error, "Failed to load work-pattern assignments.");
  }
}

/**
 * Assign a reusable rotating work pattern to an employee.
 */
export async function savePatternAssignment(userId, payload) {
  try {
    const response = await apiClient.put(
      `/hr/work-schedules/${userId}/pattern-assignment`,
      payload || {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );
    return response.data;
  } catch (error) {
    throwWithContext("savePatternAssignment", error, "Failed to assign rotating work pattern.");
  }
}

/**
 * Deactivate one active rotating-pattern assignment.
 */
export async function deletePatternAssignment(userId, assignmentId) {
  try {
    const response = await apiClient.delete(
      `/hr/work-schedules/${userId}/pattern-assignments/${assignmentId}`,
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    throwWithContext("deletePatternAssignment", error, "Failed to clear rotating work-pattern assignment.");
  }
}

/**
 * Load exact weekly schedules for one employee.
 */
export async function fetchExactSchedules(userId, options = {}) {
  try {
    const response = await apiClient.get(
      `/hr/work-schedules/${userId}/exact-schedules`,
      buildConfig(options)
    );
    return Array.isArray(response.data?.schedules) ? response.data.schedules : [];
  } catch (error) {
    if (isRequestCanceled(error)) throw error;
    throwWithContext("fetchExactSchedules", error, "Failed to load exact weekly schedules.");
  }
}

/**
 * Save one period-based exact weekly schedule for one employee.
 */
export async function saveExactSchedule(userId, payload) {
  try {
    const response = await apiClient.put(
      `/hr/work-schedules/${userId}/exact-schedule`,
      payload || {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );
    return response.data;
  } catch (error) {
    throwWithContext("saveExactSchedule", error, "Failed to save exact weekly schedule.");
  }
}

/**
 * Apply the same exact weekly schedule to multiple employees.
 */
export async function bulkSaveExactSchedule(payload) {
  try {
    const response = await apiClient.post(
      "/hr/work-schedules/bulk-exact-schedule",
      payload || {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );
    return response.data;
  } catch (error) {
    throwWithContext("bulkSaveExactSchedule", error, "Failed to bulk apply exact weekly schedule.");
  }
}

/**
 * Delete the exact weekly schedule period that starts on effectiveFrom.
 */
export async function deleteExactSchedule(userId, effectiveFrom) {
  try {
    const response = await apiClient.delete(
      `/hr/work-schedules/${userId}/exact-schedules/${encodeURIComponent(effectiveFrom)}`,
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    throwWithContext("deleteExactSchedule", error, "Failed to delete exact weekly schedule.");
  }
}


/**
 * Load a lightweight management/audit trail for one employee.
 *
 * The backend intentionally builds this trail from the existing schedule input
 * tables instead of introducing a second audit table in this batch.  It is
 * therefore a practical management view of who/what/when changed the current
 * planning inputs, not a legal-grade diff history with every old value.
 */
export async function fetchWorkScheduleAudit(userId, params = {}, options = {}) {
  try {
    const query = new URLSearchParams();
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);

    const response = await apiClient.get(
      `/hr/work-schedules/${userId}/audit?${query.toString()}`,
      buildConfig(options)
    );

    return Array.isArray(response.data?.entries) ? response.data.entries : [];
  } catch (error) {
    if (isRequestCanceled(error)) throw error;
    throwWithContext("fetchWorkScheduleAudit", error, "Failed to load work-schedule history.");
  }
}

export default {
  fetchWorkSchedulePreview,
  saveDailyScheduleOverride,
  deleteDailyScheduleOverride,
  fetchWorkPatterns,
  fetchPatternAssignments,
  fetchWorkScheduleAudit,
  savePatternAssignment,
  deletePatternAssignment,
  fetchExactSchedules,
  saveExactSchedule,
  bulkSaveExactSchedule,
  deleteExactSchedule,
};
